import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { parseBadgeData, updateDesign as updateDesignFn } from './designer-service';
import { getPerson as getPersonFn, list as listPeople } from './people-service';
import {
  get as getFn,
  create as createFn,
  extremeBadgeValues,
  getLabels as getLabelsFn,
  searchBadgeValues,
} from './service';

const badgeQueryOptions = (eventId, badgeId, token) => ({
  queryKey: ['event', eventId, 'badge', badgeId],
  queryFn: () => getFn(eventId, badgeId, token),
});

export const useBadgeQuery = (badgeId = null, token = null, queryOptions = {}) => {
  const { eventId } = useParams();

  return useQuery({
    ...badgeQueryOptions(eventId, badgeId, token),
    ...queryOptions,
  });
};

const labelsQuery = {
  getQueryKey: (eventId, peopleEntityIds = null) => ['event', eventId, 'labels', peopleEntityIds],
  queryFn: ({ queryKey: [, eventId, , peopleEntityIds] }) => getLabelsFn(eventId, peopleEntityIds),
};

export const useLabelsQuery = (peopleEntityIds = null) => {
  const { eventId } = useParams();

  return useQuery({
    queryKey: labelsQuery.getQueryKey(eventId, peopleEntityIds),
    queryFn: labelsQuery.queryFn,
  });
};

const createBadgeMutationOptions = eventId => ({
  mutationFn: payload => createFn(eventId, payload),
});

export const useCreateBadgeMutation = () => {
  const { eventId } = useParams();

  return useMutation(createBadgeMutationOptions(eventId));
};

export const useUpdateBadgeMutation = () => {
  const { eventId, badgeId } = useParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: payload => {
      /*
        Our image processing needs to check against the saved data we're based on.

        Grabbing this data here because this mutation is used from more than one place,
        so it would be silly to pass the base data around to get it to them.

        TODO: With the UI refresh, we should be down to just one place using this
        mutation and then this getQueryData could perhaps be moved out.
      */
      const prevData = parseBadgeData(
        queryClient.getQueryData(badgeQueryOptions(eventId, badgeId).queryKey),
      );

      return updateDesignFn(eventId, badgeId, payload, prevData);
    },
    onSuccess: result =>
      queryClient.setQueryData(badgeQueryOptions(eventId, badgeId).queryKey, result),
  });
};

export const usePersonQuery = (eventId, personId, token, options = {}) =>
  useQuery({
    queryKey: ['event', eventId, 'person', personId],
    queryFn: () => getPersonFn(eventId, personId, token),
    ...options,
  });

export const useBadgePeopleQuery = ({ id: badgeId, appliedTo }, params, queryOptions = {}) => {
  const { eventId } = useParams();

  return useQuery({
    queryKey: ['event', eventId, 'badge', badgeId, 'people', 'total', appliedTo],
    queryFn: () => listPeople(eventId, { appliedTo }, params),
    ...queryOptions,
  });
};

const searchBadgeValueOptions = (eventId, badgeId, params) => ({
  queryKey: ['event', eventId, 'badge', badgeId, 'people', params],
  queryFn: () => searchBadgeValues(eventId, badgeId, params),
  enabled: !!params.search,
});

export const useSearchBadgeValueQuery = (badgeId, params) => {
  const { eventId } = useParams();

  return useQuery(searchBadgeValueOptions(eventId, badgeId, params));
};

const extremeBadgeValueOptions = (eventId, badgeId, field) => ({
  queryKey: ['event', eventId, 'badge', badgeId, 'people', 'extreme', field],
  queryFn: () => extremeBadgeValues(eventId, badgeId, field),
  select: data => ({
    shortest: data.find(({ type }) => type === 'shortest'),
    longest: data.find(({ type }) => type === 'longest'),
  }),
});

export const useExtremeBadgeValueQuery = (badgeId, field, queryOptions = {}) => {
  const { eventId } = useParams();

  return useQuery({ ...extremeBadgeValueOptions(eventId, badgeId, field), ...queryOptions });
};
